// Conditionally import AppDynamics React Native agent only on mobile
// Configuration aligned with index.js settings
let AppdInstrumentation: any = undefined;
let isMobile = false;
let isWeb = false;

// AppDynamics configuration - aligned with index.js
export const APPDYNAMICS_CONFIG = {
  appKey: "AD-AAB-ADZ-DUP",
  collectorURL: "https://col.eum-appdynamics.com",
  screenshotURL: "https://image.eum-appdynamics.com",
  sessionReplayURL: "https://blob-service.eum-appdynamics.com",
};

try {
  // Only require if running in React Native (not web)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  if (typeof navigator !== "undefined" && navigator.product === "ReactNative") {
    // Check if the module is available before requiring it
    try {
      AppdInstrumentation =
        require("@appdynamics/react-native-agent").Instrumentation;
      isMobile = true;
    } catch (requireError) {
      console.warn(
        "AppDynamics React Native agent not available:",
        requireError
      );
    }
  } else if (typeof window !== "undefined") {
    isWeb = true;
  }
} catch (e) {
  // Ignore if not available (web build or other environments)
  console.warn("Platform detection failed:", e);
}
// Extend the Window interface to include AppDynamics Browser RUM API for TypeScript
declare global {
  interface Window {
    ADRUM?: {
      addUserData?: (key: string, value: any) => void;
      addPageData?: (key: string, value: any) => void;
      reportError?: (errorObj: Error, severity?: string) => void;
      reportMetric?: (
        name: string,
        value: number,
        aggregatorType?: string
      ) => void;
      markTimelineEvent?: (eventName: string, details?: any) => void;
    };
    appdynamics?: {
      reportError?: (data: any) => void;
    };
  }
}
// src/utils/Instrumentation.ts

export type ErrorSeverity = "ERROR" | "WARN" | "INFO";

export interface ReportErrorOptions {
  severity: ErrorSeverity;
  message: string;
  stackTrace?: string;
  [key: string]: any;
}

/**
 * Unified AppDynamics instrumentation for both React Native and Web
 * Provides a consistent API across platforms while using platform-specific implementations
 */
import store from "../store";

// Previous user and location state for comparison
let previousUser: any = null;
let previousLocation: any = null;
let isStoreListenerInitialized = false;

/**
 * Initialize Redux store listener to automatically update AppDynamics
 * when user authentication or location state changes
 */
export function initializeStoreListener() {
  if (isStoreListenerInitialized) {
    console.log(`[AppDynamics] Store listener already initialized`);
    return;
  }

  console.log(`[AppDynamics] Initializing Redux store listener...`);

  // Subscribe to store changes
  store.subscribe(() => {
    try {
      const state = store.getState();
      const currentUser = state?.auth?.user;
      const currentLocation = state?.appData?.selectedLocation;

      // Check if user state changed
      const userChanged =
        JSON.stringify(currentUser) !== JSON.stringify(previousUser);
      const locationChanged =
        JSON.stringify(currentLocation) !== JSON.stringify(previousLocation);

      if (userChanged) {
        console.log(`[AppDynamics] User state changed:`, {
          previous: previousUser,
          current: currentUser,
        });
        previousUser = currentUser;

        // Initialize user context when user changes
        setTimeout(() => initializeUserContext(), 100);
      }

      if (locationChanged) {
        console.log(`[AppDynamics] Location state changed:`, {
          previous: previousLocation,
          current: currentLocation,
        });
        previousLocation = currentLocation;

        // Initialize user context when location changes
        setTimeout(() => {
          initializeUserContext();
          setAppDynamicsUserData(); // Also call dedicated BRUM function
        }, 100);
      }
    } catch (error) {
      console.error(`[AppDynamics] Error in store listener:`, error);
    }
  });

  isStoreListenerInitialized = true;
  console.log(`[AppDynamics] Redux store listener initialized successfully`);

  // Initialize current state
  const state = store.getState();
  previousUser = state?.auth?.user;
  previousLocation = state?.appData?.selectedLocation;
}
export const Platform = {
  isMobile,
  isWeb,
  isAppDynamicsAvailable: () => {
    if (isMobile) {
      return (
        AppdInstrumentation &&
        typeof AppdInstrumentation.reportError === "function"
      );
    }
    if (isWeb) {
      return (
        typeof window !== "undefined" &&
        window.ADRUM &&
        typeof window.ADRUM.reportError === "function"
      );
    }
    return false;
  },
};

/**
 * Unified user data management across platforms
 */
export function setUserData(key: string, value: any): boolean {
  console.log(`[AppDynamics] Setting user data: ${key} = ${value}`);

  if (isMobile && AppdInstrumentation) {
    console.log(
      `[AppDynamics] Mobile platform detected, AppdInstrumentation available`
    );
    // React Native AppDynamics - use setUserData as primary method
    try {
      if (typeof AppdInstrumentation.setUserData === "function") {
        console.log(`[AppDynamics] Using setUserData method`);
        AppdInstrumentation.setUserData(key, value);
        console.log(
          `[AppDynamics] ✅ Successfully set user data: ${key} = ${value}`
        );
        return true;
      } else if (typeof AppdInstrumentation.setCustomMetadata === "function") {
        console.log(`[AppDynamics] Using setCustomMetadata method`);
        AppdInstrumentation.setCustomMetadata(key, value);
        console.log(
          `[AppDynamics] ✅ Successfully set custom metadata: ${key} = ${value}`
        );
        return true;
      } else if (typeof AppdInstrumentation.addUserData === "function") {
        console.log(`[AppDynamics] Using addUserData method`);
        AppdInstrumentation.addUserData(key, value);
        console.log(
          `[AppDynamics] ✅ Successfully added user data: ${key} = ${value}`
        );
        return true;
      } else if (typeof AppdInstrumentation.reportUserData === "function") {
        console.log(`[AppDynamics] Using reportUserData method`);
        AppdInstrumentation.reportUserData(key, value);
        console.log(
          `[AppDynamics] ✅ Successfully reported user data: ${key} = ${value}`
        );
        return true;
      } else if (
        typeof AppdInstrumentation.setUserId === "function" &&
        key === "email"
      ) {
        console.log(`[AppDynamics] Using setUserId method for email`);
        AppdInstrumentation.setUserId(value);
        console.log(`[AppDynamics] ✅ Successfully set user ID: ${value}`);
        return true;
      } else {
        console.log(
          `[AppDynamics] Available methods:`,
          Object.keys(AppdInstrumentation)
        );
        console.warn(
          `[AppDynamics] No suitable user data method found for key: ${key}`
        );
        return false;
      }
    } catch (error) {
      console.error(`[AppDynamics] Error setting user data:`, error);
      return false;
    }
  } else if (isWeb && window.ADRUM && window.ADRUM.addUserData) {
    console.log(`[AppDynamics] Web platform detected, using ADRUM.addUserData`);
    // Web Browser RUM
    try {
      window.ADRUM.addUserData(key, value);
      console.log(
        `[AppDynamics] ✅ Successfully set web user data: ${key} = ${value}`
      );
      return true;
    } catch (error) {
      console.error(`[AppDynamics] Error setting web user data:`, error);
      return false;
    }
  } else {
    console.log(
      `[AppDynamics] Platform detection: isMobile=${isMobile}, isWeb=${isWeb}, AppdInstrumentation=${!!AppdInstrumentation}, window.ADRUM=${!!(
        typeof window !== "undefined" && window.ADRUM
      )}`
    );
    return false;
  }
}

/**
 * Unified metric reporting across platforms
 */
export function reportMetric(
  name: string,
  value: number,
  aggregatorType: "AVERAGE" | "SUM" | "OBSERVED" = "OBSERVED"
) {
  if (
    isMobile &&
    AppdInstrumentation &&
    typeof AppdInstrumentation.reportMetric === "function"
  ) {
    // React Native AppDynamics
    AppdInstrumentation.reportMetric(name, value, aggregatorType);
  } else if (isWeb && window.ADRUM && window.ADRUM.reportMetric) {
    // Web Browser RUM
    window.ADRUM.reportMetric(name, value, aggregatorType);
  } else {
    // Fallback logging
    console.log(`[METRIC] ${name}: ${value} (${aggregatorType})`);
  }
}

/**
 * Unified timeline event marking across platforms
 */
export function markTimelineEvent(eventName: string, details?: any) {
  if (isMobile && AppdInstrumentation) {
    // React Native AppDynamics - try different API methods
    if (typeof AppdInstrumentation.startTimer === "function") {
      AppdInstrumentation.startTimer(eventName);
    } else if (typeof AppdInstrumentation.markTimelineEvent === "function") {
      AppdInstrumentation.markTimelineEvent(eventName, details);
    } else if (typeof AppdInstrumentation.leaveBreadcrumb === "function") {
      AppdInstrumentation.leaveBreadcrumb(eventName, details);
    }
  } else if (isWeb && window.ADRUM && window.ADRUM.markTimelineEvent) {
    // Web Browser RUM
    window.ADRUM.markTimelineEvent(eventName, details);
  } else {
    // Fallback logging
    console.log(`[TIMELINE] ${eventName}:`, details);
  }
}

/**
 * Unified session management across platforms
 */
export function startSession(sessionName?: string) {
  if (
    isMobile &&
    AppdInstrumentation &&
    typeof AppdInstrumentation.startSession === "function"
  ) {
    AppdInstrumentation.startSession(sessionName);
  } else if (isWeb && window.ADRUM && window.ADRUM.addPageData) {
    window.ADRUM.addPageData("sessionName", sessionName || "WebSession");
  }
}

/**
 * Unified crash reporting across platforms
 */
export function reportCrash(error: Error, isFatal: boolean = false) {
  if (
    isMobile &&
    AppdInstrumentation &&
    typeof AppdInstrumentation.reportCrash === "function"
  ) {
    AppdInstrumentation.reportCrash(error, isFatal);
  } else {
    // Use regular error reporting for web
    reportError({
      severity: isFatal ? "ERROR" : "WARN",
      message: error.message,
      stackTrace: error.stack,
      isCrash: true,
      isFatal,
    });
  }
}

/**
 * Unified custom timer management
 */
export class AppDynamicsTimer {
  private timerName: string;
  private startTime: number;
  private nativeTimer: any;

  constructor(timerName: string) {
    this.timerName = timerName;
    this.startTime = Date.now();

    if (
      isMobile &&
      AppdInstrumentation &&
      typeof AppdInstrumentation.startTimer === "function"
    ) {
      this.nativeTimer = AppdInstrumentation.startTimer(timerName);
    }
  }

  stop(properties?: any) {
    const duration = Date.now() - this.startTime;

    if (
      isMobile &&
      this.nativeTimer &&
      typeof this.nativeTimer.stop === "function"
    ) {
      this.nativeTimer.stop(properties);
    } else if (
      isMobile &&
      AppdInstrumentation &&
      typeof AppdInstrumentation.stopTimer === "function"
    ) {
      AppdInstrumentation.stopTimer(this.timerName, properties);
    }

    // Always report as metric for consistency
    reportMetric(`timer.${this.timerName}`, duration, "AVERAGE");

    // Mark timeline event
    markTimelineEvent(`Timer Completed: ${this.timerName}`, {
      duration,
      ...properties,
    });

    return duration;
  }
}

/**
 * Factory function to create timers
 */
export function createTimer(timerName: string): AppDynamicsTimer {
  return new AppDynamicsTimer(timerName);
}

/**
 * Initialize AppDynamics with the same configuration as index.js
 * This ensures consistency across the application
 */
export function initializeAppDynamics() {
  if (
    isMobile &&
    AppdInstrumentation &&
    typeof AppdInstrumentation.start === "function"
  ) {
    // Use the same configuration as index.js
    AppdInstrumentation.start({
      appKey: APPDYNAMICS_CONFIG.appKey,
      collectorURL: APPDYNAMICS_CONFIG.collectorURL,
      screenshotURL: APPDYNAMICS_CONFIG.screenshotURL,
      sessionReplayURL: APPDYNAMICS_CONFIG.sessionReplayURL,
    });
    console.log("AppDynamics mobile RUM started with aligned configuration");
  } else if (isWeb) {
    // Web initialization is handled by appDynamicsInit.js
    console.log(
      "AppDynamics web RUM should be initialized via appDynamicsInit.js"
    );
  }
}

/**
 * Initializes user context from Redux store for AppDynamics session
 * Works across both platforms with aligned configuration
 * Includes retry logic for mobile timing issues
 */
export function initializeUserContext(retryCount: number = 0) {
  console.log(
    `[AppDynamics] Initializing user context... (attempt ${retryCount + 1})`
  );

  try {
    const state = store.getState();
    const user = state?.auth?.user;
    const location = state?.appData?.selectedLocation;

    console.log(`[AppDynamics] User from Redux:`, user);
    console.log(`[AppDynamics] Location from Redux:`, location);
    console.log(
      `[AppDynamics] Full user object structure:`,
      JSON.stringify(user, null, 2)
    );

    // Set platform-specific session data with aligned configuration
    if (isMobile && AppdInstrumentation) {
      console.log(`[AppDynamics] Mobile initialization...`);
      if (typeof AppdInstrumentation.startSession === "function") {
        AppdInstrumentation.startSession("LighthouseMobileSession");
        console.log(
          `[AppDynamics] Started mobile session: LighthouseMobileSession`
        );
      }
      // Ensure mobile app uses same configuration as index.js
      console.log(
        `AppDynamics mobile initialized with appKey: ${APPDYNAMICS_CONFIG.appKey}`
      );
    } else if (isWeb && window.ADRUM && window.ADRUM.addPageData) {
      console.log(`[AppDynamics] Web initialization...`);
      window.ADRUM.addPageData("application", "Lighthouse-Web");
      window.ADRUM.addPageData("platform", "web");
      window.ADRUM.addPageData("appKey", APPDYNAMICS_CONFIG.appKey);
      window.ADRUM.addPageData("collectorURL", APPDYNAMICS_CONFIG.collectorURL);
    }

    let userDataSet = false;

    if (user) {
      console.log(`[AppDynamics] Setting user data:`, user);

      // Try multiple common user ID fields
      const possibleUserFields = [
        "email",
        "mail",
        "preferred_username",
        "upn",
        "username",
        "name",
        "unique_name",
      ];
      let userId = null;

      for (const field of possibleUserFields) {
        if (user[field]) {
          userId = user[field];
          console.log(
            `[AppDynamics] Found user ID in field '${field}': ${userId}`
          );
          break;
        }
      }

      if (userId) {
        const success1 = setUserData("email", userId);
        const success2 = setUserData("customerId", userId); // Add customerId for web BRUM
        const success3 = setUserData(
          "UserName",
          user.name || user.displayName || userId
        );
        const success4 = setUserData(
          "customerName",
          user.name || user.displayName || userId
        ); // Add customerName for web BRUM
        const success5 = setUserData("UserRole", user.role || "User");

        userDataSet = success1 || success2 || success3 || success4 || success5;
      } else {
        console.log(
          `[AppDynamics] No valid user ID found in available fields:`,
          possibleUserFields
        );
      }
    } else {
      console.log(`[AppDynamics] No user data found in Redux store`);
    }

    if (location) {
      console.log(`[AppDynamics] Setting location data:`, location);
      setUserData("Location Id", location.id || location.locationId);
      setUserData("Location Name", location.name || location.locationName);
      setUserData("Location Type", location.type);
    }

    // Set platform info with aligned configuration
    setUserData("platform", isMobile ? "mobile" : "web");
    setUserData("appVersion", process.env.REACT_APP_VERSION || "1.0.0");
    setUserData("appKey", APPDYNAMICS_CONFIG.appKey);

    // Retry logic for mobile if user data wasn't set successfully
    if (isMobile && user && !userDataSet && retryCount < 3) {
      console.log(
        `[AppDynamics] User data not set successfully on mobile, retrying in 500ms...`
      );
      setTimeout(() => initializeUserContext(retryCount + 1), 500);
      return;
    }

    console.log(
      `AppDynamics initialized for ${
        isMobile ? "mobile" : "web"
      } platform with aligned configuration`
    );
  } catch (e) {
    console.warn("Failed to initialize AppDynamics user context:", e);

    // Retry on error if we haven't exceeded retry limit
    if (retryCount < 2) {
      console.log(`[AppDynamics] Retrying initialization in 1000ms...`);
      setTimeout(() => initializeUserContext(retryCount + 1), 1000);
    }
  }
}

export function reportError({
  severity,
  message,
  stackTrace,
  ...rest
}: ReportErrorOptions) {
  // Try to get user and location info from Redux store
  let userId: string | undefined = undefined;
  let location: any = undefined;
  try {
    const state = store.getState();
    const user = state?.auth?.user;
    // Try common fields for email/username
    userId =
      user?.email ||
      user?.mail ||
      user?.preferred_username ||
      user?.upn ||
      user?.username ||
      user?.name;
    // Try to get location info (id and name if available)
    const loc = state?.appData?.selectedLocation;
    if (loc) {
      location = {
        id: loc.id || loc.locationId,
        name: loc.name || loc.locationName,
        ...loc,
      };
    }
  } catch (e) {
    // ignore
  }

  // --- AppDynamics Mobile RUM Integration for React Native ---
  if (
    isMobile &&
    AppdInstrumentation &&
    typeof AppdInstrumentation.reportError === "function"
  ) {
    // Compose an Error object to capture stack and context
    const errorObj: any = new Error(message);
    if (stackTrace) errorObj.stack = stackTrace;
    errorObj.severity = severity;
    errorObj.userId = userId;
    errorObj.location = location;
    Object.assign(errorObj, rest);
    // Map local severity to AppDynamics expected values
    let appdSeverity: any = "error";
    if (severity === "WARN") appdSeverity = "warning";
    else if (severity === "INFO") appdSeverity = "info";
    AppdInstrumentation.reportError(errorObj, appdSeverity);
  }
  // --- AppDynamics Browser RUM Integration for Web ---
  else if (
    typeof window !== "undefined" &&
    window.ADRUM &&
    typeof window.ADRUM.reportError === "function"
  ) {
    // Create Error object for AppDynamics Browser RUM
    const errorObj = new Error(message);
    if (stackTrace) errorObj.stack = stackTrace;

    // Add user and location data to AppDynamics session
    try {
      if (userId && window.ADRUM && window.ADRUM.addUserData) {
        // For web platform, use customerId instead of userId
        window.ADRUM.addUserData("customerId", userId);
        console.log(`[AppDynamics] Set customerId: ${userId}`);
      }
      if (location && window.ADRUM && window.ADRUM.addUserData) {
        window.ADRUM.addUserData("locationId", location.id);
        window.ADRUM.addUserData("locationName", location.name);
        console.log(
          `[AppDynamics] Set location data: ${location.id}, ${location.name}`
        );
      }

      // Add customer name if available
      const state = store.getState();
      const user = state?.auth?.user;
      const customerName = user?.name || user?.displayName || userId;
      if (customerName && window.ADRUM && window.ADRUM.addUserData) {
        window.ADRUM.addUserData("customerName", customerName);
        console.log(`[AppDynamics] Set customerName: ${customerName}`);
      }
    } catch (e) {
      console.error(`[AppDynamics] Error setting user data:`, e);
    }

    // Add custom properties as user data
    Object.keys(rest).forEach((key) => {
      if (window.ADRUM && window.ADRUM.addUserData) {
        window.ADRUM.addUserData(key, rest[key]);
      }
    });

    // Map local severity to AppDynamics expected values
    let appdSeverity: string = "ERROR";
    if (severity === "WARN") appdSeverity = "WARNING";
    else if (severity === "INFO") appdSeverity = "INFO";

    // Report error to AppDynamics Browser RUM
    window.ADRUM.reportError(errorObj, appdSeverity);

    // Also mark as timeline event for better visibility
    if (window.ADRUM.markTimelineEvent) {
      window.ADRUM.markTimelineEvent(`Error: ${severity}`, {
        message,
        userId,
        location: location?.name || "Unknown",
        ...rest,
      });
    }
  }
  // Fallback for custom appdynamics implementation
  else if (
    typeof window !== "undefined" &&
    window.appdynamics &&
    typeof window.appdynamics.reportError === "function"
  ) {
    window.appdynamics.reportError({
      severity,
      message,
      stackTrace,
      userId,
      location,
      ...rest,
    });
  } else {
    // Fallback: log to console for demonstration
    console.error(`fallback [${severity}]`, message, stackTrace, {
      userId,
      location,
      ...rest,
    });
  }
}

/**
 * Test function to check AppDynamics user data on mobile
 * Call window.testAppDynamicsUserData() in mobile console
 */
export const testAppDynamicsUserData = () => {
  const isMobile =
    typeof window !== "undefined" &&
    window.navigator &&
    /Mobile|Android|iPhone|iPad/.test(window.navigator.userAgent);

  console.log(`[AppDynamics] Test function called - isMobile: ${isMobile}`);

  if (isMobile && AppdInstrumentation) {
    console.log(`[AppDynamics] Mobile AppDynamics available`);
    console.log(
      `[AppDynamics] Available methods:`,
      Object.keys(AppdInstrumentation)
    );

    // Test setting user data
    try {
      if (typeof AppdInstrumentation.setUserData === "function") {
        console.log(`[AppDynamics] Testing setUserData...`);
        AppdInstrumentation.setUserData("test_key", "test_value");
        console.log(`[AppDynamics] ✅ setUserData test successful`);
      }

      if (typeof AppdInstrumentation.setUserId === "function") {
        console.log(`[AppDynamics] Testing setUserId...`);
        AppdInstrumentation.setUserId("test_user_id");
        console.log(`[AppDynamics] ✅ setUserId test successful`);
      }

      return true;
    } catch (error) {
      console.error(`[AppDynamics] ❌ Test error:`, error);
      return false;
    }
  } else {
    console.log(`[AppDynamics] Not mobile or AppDynamics not available`);
    return false;
  }
};

/**
 * Manual test function for console testing
 */
export const manualTestUserData = () => {
  console.log(`[AppDynamics] Manual test starting...`);
  console.log(`[AppDynamics] Current store state:`, store.getState());
  initializeUserContext();
  testAppDynamicsUserData();
};

// Make test functions available globally for console testing
if (typeof window !== "undefined") {
  (window as any).testAppDynamicsUserData = testAppDynamicsUserData;
  (window as any).manualTestUserData = manualTestUserData;
  (window as any).initializeStoreListener = initializeStoreListener;
  (window as any).initializeUserContext = initializeUserContext;
  (window as any).setAppDynamicsUserData = setAppDynamicsUserData;
}

// Auto-initialize store listener when module loads
if (typeof window !== "undefined") {
  // Initialize after a short delay to ensure store is ready
  setTimeout(() => {
    initializeStoreListener();
    console.log(`[AppDynamics] Store listener auto-initialized`);
  }, 100);
}

/**
 * Dedicated function to set user data for AppDynamics BRUM
 * Call this after user authentication to ensure customerId and customerName are set
 */
export function setAppDynamicsUserData() {
  console.log(`[AppDynamics] Setting dedicated user data for BRUM...`);

  try {
    const state = store.getState();
    const user = state?.auth?.user;
    const location = state?.appData?.selectedLocation;

    if (isWeb && window.ADRUM && window.ADRUM.addUserData) {
      if (user) {
        // Try multiple common user ID fields
        const possibleUserFields = [
          "email",
          "mail",
          "preferred_username",
          "upn",
          "username",
          "name",
          "unique_name",
        ];
        let userId = null;

        for (const field of possibleUserFields) {
          if (user[field]) {
            userId = user[field];
            console.log(
              `[AppDynamics] Found user ID in field '${field}': ${userId}`
            );
            break;
          }
        }

        if (userId) {
          window.ADRUM.addUserData("customerId", userId);
          console.log(`[AppDynamics] ✅ Set customerId: ${userId}`);
        }

        const customerName = user.name || user.displayName || userId;
        if (customerName) {
          window.ADRUM.addUserData("customerName", customerName);
          console.log(`[AppDynamics] ✅ Set customerName: ${customerName}`);
        }

        // Set additional user properties
        if (user.role) {
          window.ADRUM.addUserData("userRole", user.role);
        }
      }

      if (location) {
        window.ADRUM.addUserData(
          "locationId",
          location.id || location.locationId
        );
        window.ADRUM.addUserData(
          "locationName",
          location.name || location.locationName
        );
        if (location.type) {
          window.ADRUM.addUserData("locationType", location.type);
        }
      }

      // Set app metadata
      window.ADRUM.addUserData("platform", "web");
      window.ADRUM.addUserData(
        "appVersion",
        process.env.REACT_APP_VERSION || "1.0.0"
      );

      console.log(`[AppDynamics] ✅ User data set successfully for BRUM`);
      return true;
    } else {
      console.log(`[AppDynamics] BRUM not available or not web platform`);
      return false;
    }
  } catch (error) {
    console.error(`[AppDynamics] Error setting user data for BRUM:`, error);
    return false;
  }
}
